import * as Sentry from '@sentry/nuxt'
import { onScopeDispose } from 'vue'
import { useRoute } from '#app/composables/router'
import { useNuxtApp } from '#app/nuxt'
import { useCurrentShop } from '#storefront/composables'

import type useNewPlp from './useNewPlp'
import { shouldReconcilePlp } from '../../utils/shouldReconcilePlp'
import {
  buildPlpEmptyReport,
  PLP_EMPTY_REASON,
} from '../../utils/plpObservability'

type PlpState = ReturnType<typeof useNewPlp>

/**
 * Post-hydration self-heal for the PLP browser-back hydration race (ECOM-2692).
 *
 * When the app boots fresh on PLP A and the user hits browser-back to PLP B
 * inside the hydration window, every client fetch trigger is missed: the
 * `flush:'post'` route watcher in ProductListingPage is paused while the page's
 * Suspense boundary is pending, the one-shot `fetchOnNavigation` already ran for
 * A, and `onMounted` runs once. B is left with no category data and an empty
 * product slice.
 *
 * A single `reconcile()` — shared by two triggers so there is one fetch latch,
 * never two racing fetchers — checks whether the active route's data actually
 * loaded and heals it, picking depth by what's missing: a full `useNewPlp.fetch()`
 * (category + CMS + filters + products) when the category never materialized (the
 * race — so title/meta heal too, R6), or the lighter products-only
 * `fetchProducts()` when the category is present and only the client-execution
 * product fetch is outstanding (the ordinary fresh-PLP load — a full fetch there
 * would redundantly re-fetch the SSR'd category/filters, R3). The decision is the
 * pure `shouldReconcilePlp`, which suppresses genuine empty-filter results and a
 * confirmed-absent (404) category. It covers both the Odoscope and Scayle-fallback
 * paths (gated on client-execution, not Odoscope) so sorted/filtered PLPs heal too.
 *
 * Two complementary triggers feed the one reconcile():
 *   - `app:suspense:resolve` fires once the initial hydration completes — the
 *     point at which a browser-back transition swallowed during hydration has
 *     settled. This is the trigger for the actual race.
 *   - `onMounted` (in ProductListingPage) covers PLPs mounted *after* hydration
 *     (`$hydrated` already true), where `app:suspense:resolve` won't fire again.
 * SPA navigation between already-mounted PLPs is handled by the existing
 * (post-hydration, un-paused) route watcher. `useNewPlp.fetch()` is re-entrancy
 * guarded, so overlapping triggers can't double-fetch.
 */
export default (plp: PlpState) => {
  const nuxtApp = useNuxtApp()
  const route = useRoute()
  const currentShop = useCurrentShop()

  const reconcile = async () => {
    const hasCategoryData = Boolean(plp.selectedCategory.value)

    const needsHeal = shouldReconcilePlp({
      isPlpEnabled: plp.isClientExecution.value,
      // $hydrated is provided by the client-only 12.hydrationReady plugin, so it
      // is undefined on the server — reconcile only ever runs client-side.
      isHydrated: Boolean(nuxtApp.$hydrated?.value),
      isFetchInFlight: plp.isFetchInFlight.value,
      fetched: plp.productsSettled.value,
      hasProducts: plp.products.value.length > 0,
      hasCategoryData,
      categoryResolutionAttempted: plp.categoryResolutionAttempted.value,
    })

    if (!needsHeal) {
      return
    }

    try {
      if (!hasCategoryData) {
        // The race: category never loaded, so everything is missing. Full fetch
        // heals category + CMS + filters + products together (R6); the fetch's
        // routeProductsRefreshing keeps the skeleton up for its duration so the
        // genuine empty state never flashes (R7).
        //
        // Observability (U7): record that the race occurred and the heal fired —
        // the primary production confirmation, since the window resists
        // deterministic reproduction. Shares the empty-PLP Sentry tag shape but
        // with an accurate message (this is a heal, not an empty render).
        const report = buildPlpEmptyReport(
          PLP_EMPTY_REASON.hydrationRaceReconciled,
          {
            routeFullPath: route.fullPath,
            shopId: currentShop.value?.shopId,
            productsSettled: plp.productsSettled.value,
          },
          'PLP browser-back hydration race healed',
        )
        Sentry.captureMessage(report.message, {
          level: report.level,
          tags: report.tags,
          extra: report.extra,
        })

        await plp.fetch()
      } else {
        // Ordinary fresh client-execution PLP: category present, only the client
        // product fetch is outstanding. Products-only keeps the load light (R3);
        // the source-level in-flight guards dedupe against any concurrent trigger.
        await plp.fetchProducts()
      }
    } catch {
      // plp.fetch() can throw a Nuxt showError (404 when the category is genuinely
      // absent). showError has already set the error page, so swallow the re-throw
      // here — otherwise it surfaces as an unhandled rejection out of the
      // app:suspense:resolve hook or the async onMounted. The
      // categoryResolutionAttempted gate prevents a retry loop on a real 404.
    }
  }

  if (import.meta.client) {
    // hookable returns an unsubscribe fn; release it when the page's effect scope
    // disposes so a handler bound to this plp instance can't accumulate across
    // mounts or fire against a later route.
    const unhook = nuxtApp.hook('app:suspense:resolve', reconcile)
    onScopeDispose(() => unhook())
  }

  return { reconcile }
}
